import { downgradeComponent, downgradeInjectable } from "@angular/upgrade/static";
import { SideBarComponent } from "@app/directives/_layout/_page-design/sidebar/sidebar.component";
import { AccountFeatures } from "@enums/account/AccountFeature";
import { AuthorizationService } from "@services/authorization.service";
import { RouteConfigHelperProvider } from "@services/routeConfigHelper.provider";
import { DriverActivitySheetComponent } from "@tms/_components/driverActivitySheet/driverActivitySheet.component";
import { PvaBarChartComponent } from "@tms/_components/pva-bar-chart/pva-bar-chart.component";
import { PvaChartLegend } from "@tms/_components/pva-bar-chart/pva-chart-legend.component";
import { RoutePerformanceBarComponent } from "@tms/_components/route-performance-bar/route-performance-bar.component";
import { RouteDetailsComponent } from "@tms/_components/routeDetails/routeDetails.component";
import { ActiveRouteProgressComponent } from "@tms/_routes/activeRoutes/active-route-progress/active-route-progress.component";
import { ActiveRouteVisitComponent } from "@tms/_routes/activeRoutes/active-route-visit/active-route-visit.component";
import { ActiveRoutesComponent } from "@tms/_routes/activeRoutes/active-routes.component";
import { ArrivalsBoardComponent } from "@tms/_routes/arrivalsBoard/arrivalsBoard.component";
import { HistoryNotesComponent } from "@tms/_routes/history/notes/notes.component";
import { TmsHistoryComponent } from "@tms/_routes/history/tms-history.component";
import { RoutePerformanceComponent } from "@tms/_routes/route-performance/route-performance.component";
import { ScheduleImportComponent } from "@tms/_routes/scheduling/_components/schedule-import/schedule-import.component";
import { ScheduleEditorComponent } from "@tms/_routes/scheduling/_components/scheduleEditor/scheduleEditor.component";
import { ScheduleVisitsListComponent } from "@tms/_routes/scheduling/_components/scheduleVisitsList/scheduleVisitsList.component";
import { ScheduleVisitsListItemComponent } from "@tms/_routes/scheduling/_components/scheduleVisitsListItem/scheduleVisitsListItem.component";
import { ScheduleVisitValidationComponent } from "@tms/_routes/scheduling/_components/scheduleVisitValidation/scheduleVisitValidation.component";
import { SchedulingComponent } from "@tms/_routes/scheduling/scheduling.component";
import { VisitPerformanceComponent } from "@tms/_routes/visit-performance/visit-performance.component";
import { ActiveRoutesService } from "@tms/_services/active-routes.service";
import { PlannedRouteAuditService } from "@tms/_services/planned-route-audit.service";
import { PlannedRouteMapService } from "@tms/_services/planned-route-map.service";
import { PlannedRouteNoteService } from "@tms/_services/planned-route-note.service";
import { PlannedRouteVisitService } from "@tms/_services/planned-route-visit.service";
import { PlannedRouteService } from "@tms/_services/planned-route.service";
import { SchedulingService } from "@tms/_services/scheduling.service";
import { UnplannedStopService } from "@tms/_services/unplanned-stop.service";
import { TmsModuleNavigation } from "@tms/tms.navigation";
import { StateProvider } from "@uirouter/angularjs";
import * as angular from "angular";
import { CloseRouteReasonComponent } from "./_components/close-route/close-route-reason.component";
import { PlannedRouteService2 } from "./_services/planned-route.service2";

export class Config {

    static $inject = ["$stateProvider", "routeConfigHelperProvider"];

    constructor(
        private $stateProvider: StateProvider,
        private routeConfigHelperProvider: RouteConfigHelperProvider
    ) {
        $stateProvider.state(TmsModuleNavigation.ActiveRoutes, {
            url: "/activeRoutes",
            views: { "content@app": { component: "activeRoutes" } },
            data: { translation: 'Tms.ActiveRoutes', subtitle: "See currently active routes or planned routes that will start soon" }
        });

        $stateProvider.state(TmsModuleNavigation.ArrivalsBoard, {
            url: "/arrivalsBoard",
            views: { "content@app": { component: "arrivalsBoard" } },
            data: { translation: 'Tms.ArrivalsBoard', subtitle: "See when routes will complete" }
        });

        $stateProvider.state(TmsModuleNavigation.History, {
            url: "/history",
            views: { "content@app": { component: "tmsHistory" } },
            data: { translation: 'Tms.History', subtitle: "Find and view historic routes" }
        });

        // ------------------------------------------------

        $stateProvider.state(TmsModuleNavigation.Scheduling, {
            url: "/scheduling",
            views: { "content@app": { component: "scheduling" } },
            data: { translation: 'Tms.Scheduling', subtitle: "Schedule, edit or batch import TMS routes" }
        });


        $stateProvider.state(TmsModuleNavigation.EditSchedule, {
            url: "/:routeId/edit",
            views: { "content-detail@app": { component: "scheduleEditor", bindings: { routeId: "routeId" } } },
            data: { translation: 'Tms.EditSchedule' },
            params: {
                routeId: {
                    squash: true,
                    value: null
                }
            },
            resolve: {
                routeId: ["$stateParams", ($stateParams) => { return $stateParams["routeId"]; }],
                authorize: ["authorizationService", (authorization: AuthorizationService) => authorization.authorizeForUser([], [], [AccountFeatures.TMS])]
            }
        });

        $stateProvider.state(TmsModuleNavigation.NewSchedule, {
            url: "/new",
            views: { "content-detail@app": { component: "scheduleEditor" } },
            data: { translation: 'Tms.CreateSchedule' },
            resolve: {
                authorize: ["authorizationService", (authorization: AuthorizationService) => authorization.authorizeForUser([], [], [AccountFeatures.TMS])]
            }
        });

        $stateProvider.state(TmsModuleNavigation.ImportSchedule, {
            url: "/import",
            views: { "content-detail@app": { component: "scheduleImport" } },
            data: { translation: 'ScheduleImport.ImportSchedule', subtitleTranslationKey: "ScheduleImport.ImportScheduleSubtitle" }
        });

        $stateProvider.state(TmsModuleNavigation.RoutePerformance, {
            url: "/route-performance",
            views: { "content@app": { component: "routePerformance" } },
            data: { translation: "Tms.RoutePerformance", subtitleTranslationKey: "Tms.RoutePerformanceSubtitle" }
        });

        $stateProvider.state(TmsModuleNavigation.VisitPerformance, {
            url: "/visit-performance",
            views: { "content@app": { component: "visitPerformance" } },
            data: { translation: "Tms.VisitPerformance", subtitleTranslationKey: "Tms.RoutePerformanceSubtitle"  }
        });

        $stateProvider.state(TmsModuleNavigation.ParentRoot, {
            abstract: true,
            url: "/tms",
            redirectTo: TmsModuleNavigation.Default,
            views: { "module-navigation": {} },
            data: { translation: 'Common.TMS' }
        });

        this.createTMSRouteView(TmsModuleNavigation.ActiveRoutes);
        this.createTMSRouteView(TmsModuleNavigation.ArrivalsBoard);
        this.createTMSRouteView(TmsModuleNavigation.History);
        this.createTMSRouteView(TmsModuleNavigation.RoutePerformance);
        this.createTMSRouteView(TmsModuleNavigation.VisitPerformance);
        this.createTMSRouteView(TmsModuleNavigation.Scheduling);
    }

    private createTMSRouteView(parentViewName: string) {
        this.routeConfigHelperProvider.createDetailStateWithBoundParams({
            stateName: `${parentViewName}.view`,
            translation: 'Tms.History',
            bindingName: "routeId",
            component: "routeDetails",
            breadcrumbNameResolverKey: "routeNameKey"
        });
    }
}

angular
    .module("app.tms", ["app.directives", "dndLists"])
    .config(Config)

    .service("plannedRouteVisitService", PlannedRouteVisitService)
    .service("plannedRouteNoteService", PlannedRouteNoteService)
    .service("unplannedStopService", UnplannedStopService)
    .service("plannedRouteService", PlannedRouteService)
    .service("plannedRouteAuditService", PlannedRouteAuditService)
    .service("plannedRouteMapService", PlannedRouteMapService)
    .service("plannedRouteService2", downgradeInjectable(PlannedRouteService2))

    .service("schedulingService", SchedulingService)
    .service("activeRoutesService", downgradeInjectable(ActiveRoutesService))

    .component("arrivalsBoard", new ArrivalsBoardComponent())
    .component("routeDetails", new RouteDetailsComponent())
    .component("driverActivitySheet", new DriverActivitySheetComponent())

    .directive("sidebar", downgradeComponent({ component: SideBarComponent}))
    .directive("tmsHistory", downgradeComponent({ component: TmsHistoryComponent }))
    .directive("historyNotes", downgradeComponent({ component: HistoryNotesComponent }))
    .directive("activeRoutes", downgradeComponent({ component: ActiveRoutesComponent }))
    .directive("activeRouteVisit", downgradeComponent({ component: ActiveRouteVisitComponent }))
    .directive("routePerformance", downgradeComponent({ component: RoutePerformanceComponent }))
    .directive("routePerformanceBar", downgradeComponent({ component: RoutePerformanceBarComponent }))
    .directive("visitPerformance", downgradeComponent({ component: VisitPerformanceComponent }))
    .directive("activeRouteProgress", downgradeComponent({ component: ActiveRouteProgressComponent }))
    .directive("pvaBarChart", downgradeComponent({ component: PvaBarChartComponent }))
    .directive("pvaChartLegend", downgradeComponent({ component: PvaChartLegend }))
    .directive("closeRouteReason", downgradeComponent({ component: CloseRouteReasonComponent }))

    // scheduling
    .component("scheduling", new SchedulingComponent())
    .directive("scheduleImport", downgradeComponent({ component: ScheduleImportComponent }))
    .component("scheduleEditor", new ScheduleEditorComponent())
    .component("scheduleVisitsList", new ScheduleVisitsListComponent())
    .component("scheduleVisitsListItem", new ScheduleVisitsListItemComponent())
    .component("scheduleVisitValidation", new ScheduleVisitValidationComponent())
